/**
 * Format time in seconds to MM:SS format
 * @param time - Time in seconds
 * @returns Formatted time string (e.g., "3:05")
 */
export const formatTime = (time: number): string => {
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * The chapter-local time a playback display (scrubber, time labels) should
 * show. While a saved position is pending the element still reads 0 — the
 * restore seek lands only once the reader has positioned itself, because the
 * audio source is withheld until then — so the pending offset wins. After that
 * the element is the live source, with React state as the fallback before an
 * element exists.
 */
export const getDisplayedPlaybackTime = (
  audio: HTMLAudioElement | null | undefined,
  currentTime: number,
  pendingResumeSeconds: number | null | undefined,
): number => pendingResumeSeconds ?? audio?.currentTime ?? currentTime;

/**
 * Formats the remaining time for a book
 * @param totalDuration - Total duration of the book in seconds
 * @param currentTime - Current progress in seconds
 * @returns Formatted string (e.g., "2h 15m left")
 */
export const formatTimeLeft = (totalDuration: number, currentTime: number): string => {
  const timeLeft = Math.max(0, totalDuration - currentTime);
  
  // If less than 0.25 seconds remain, consider it finished
  if (timeLeft <= 0.25) {
    return "Finished";
  }

  const hours = Math.floor(timeLeft / 3600);
  const minutes = Math.floor((timeLeft % 3600) / 60);
  const seconds = Math.floor(timeLeft % 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m left`;
  } else if (minutes > 0) {
    return `${minutes}m left`;
  } else {
    return `${seconds}s left`;
  }
};
